/**
 * SoleadLab 2.0 — Current-ortho-layer resolution (P2a fault detection)
 *
 * Shared by the Scan dispatch (which layer to scan), exemplar scoping, and
 * indication provenance stamping (utils/createIndication.ts).
 *
 * "Current ortho layer" = the layer the user is working against:
 *   1. the explicitly selected layer (mapStore.activeOrthophotoId, set by the
 *      Orthophoto ticket's layer switcher / Layers panel highlight) when it
 *      still exists AND is visible;
 *   2. otherwise the topmost VISIBLE layer in render-stack order (projectStore
 *      contract: orthophotos[0] = TOP, sort_order ASC — new uploads prepend).
 * Returns null when no ortho layer is visible at all.
 */

import { useProjectStore, type Orthophoto } from '../stores/projectStore';
import { useMapStore } from '../stores/mapStore';

/** Pure resolver — reactive components pass their subscribed slices. */
export function pickCurrentOrtho(
  orthophotos: Orthophoto[],
  activeOrthophotoId: string | null,
): Orthophoto | null {
  if (activeOrthophotoId) {
    const active = orthophotos.find((o) => o.id === activeOrthophotoId);
    if (active && active.visible) return active;
  }
  return orthophotos.find((o) => o.visible) ?? null;
}

/** Store-reading convenience for non-reactive callers (creation stamping). */
export function resolveCurrentOrtho(): Orthophoto | null {
  return pickCurrentOrtho(
    useProjectStore.getState().data.orthophotos,
    useMapStore.getState().activeOrthophotoId,
  );
}
